import React, { useRef, useState } from 'react';
import { Button, Tooltip } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { AuditLogText } from 'src/components/pages/CoiFormManagement/sharedFormManagement.styles';
import { useTheme } from 'styled-components';
import { getTestIdFromString } from 'src/utils';
import {
  Description,
  HeaderRight,
  InfoContentContainer,
  InfoIconButton,
  PageHeaderWithActionsStyled,
  Title,
  TitleContainer,
} from './PageHeaderWithActions.styles';

interface Props {
  createItem?: () => void;
  createButtonText?: string;
  openAuditLog?: () => void;
  tooltipText?: string;
  infoContent?:
    | React.ReactNode
    | ((
        close: () => void,
        triggerRef: React.RefObject<HTMLButtonElement | null>,
      ) => React.ReactNode);
  title: string;
  description?: string;
  customActions?: React.ReactNode;
  $minHeight?: string;
}

export const PageHeaderWithActions = ({
  createItem,
  openAuditLog,
  title,
  description,
  tooltipText,
  infoContent,
  createButtonText,
  customActions,
  $minHeight,
}: Props) => {
  const theme = useTheme();
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [infoContentVisible, setInfoContentVisible] = useState(false);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const hasTitleInfo = Boolean(tooltipText || infoContent);
  const closeInfoContent = () => setInfoContentVisible(false);

  return (
    <>
      <PageHeaderWithActionsStyled $minHeight={$minHeight}>
        {hasTitleInfo ? (
          <TitleContainer>
            <Title>{title}</Title>
            {infoContent ? (
              <>
                <InfoIconButton
                  ref={infoButtonRef}
                  type="button"
                  aria-label={`${title} information`}
                  aria-expanded={infoContentVisible}
                  onClick={() => setInfoContentVisible((visible) => !visible)}
                  data-testid="page-header-info-button"
                >
                  <StyledIcon
                    className="uil uil-info-circle"
                    color={theme.atlas.semantic.color.type.default}
                    size="24px"
                    isClickable
                  />
                </InfoIconButton>
                {infoContentVisible && (
                  <InfoContentContainer>
                    {typeof infoContent === 'function'
                      ? infoContent(closeInfoContent, infoButtonRef)
                      : infoContent}
                  </InfoContentContainer>
                )}
              </>
            ) : (
              <StyledIcon
                className="uil uil-info-circle"
                color={theme.atlas.semantic.color.type.default}
                size="24px"
                ref={setRoot}
                onMouseOver={() => {
                  setTooltipVisible(true);
                }}
                onMouseOut={() => setTooltipVisible(false)}
              />
            )}
          </TitleContainer>
        ) : (
          <div>
            <Title>{title}</Title>
            {description && <Description>{description}</Description>}
          </div>
        )}
        <HeaderRight>
          {openAuditLog && (
            <AuditLogText onClick={openAuditLog} data-testid="audit-log-button">
              Audit log
            </AuditLogText>
          )}
          {createButtonText && createItem && (
            <Button
              variant="primary"
              size="medium"
              onClick={createItem}
              data-testid={`${getTestIdFromString(createButtonText)}-button`}
            >
              <StyledIconInButton
                size="medium"
                position="left"
                className="uil uil-plus"
              />
              {createButtonText}
            </Button>
          )}
          {customActions && customActions}
        </HeaderRight>
      </PageHeaderWithActionsStyled>
      {tooltipText && !infoContent && tooltipVisible && root && (
        <Tooltip text={tooltipText} variant="up" root={root} width="250px" />
      )}
    </>
  );
};
